import React, { useMemo } from 'react';
import Button from '../components/Button';
import SVGIcon from '../components/SVGIcon';
import type { UserType } from '../graphql/generated/graphql';
import clearIcon from '../icons/non-prefilled/16x16/clear.svg';
import type {
  EntitySearchResult,
  EntitySelectConfig,
} from '../search/entitySearch/EntitySelect';
import EntitySelect from '../search/entitySearch/EntitySelect';
import { UIActions } from '../ui/uiSlice';
import handleApolloError from '../utils/error/handleApolloError';
import styles from './AddMemberSelect.module.scss';
import { useAppDispatch } from '../app/redux/useAppSelector';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';

const ADD_MEMBER_SELECT_FRAGMENT = gql(`
  fragment AddMemberSelect_group on Group {
    id
    owners {
      id
    }
    members {
      id
    }
    children {
      id
    }
  }
`);

export interface AddMemberSelectProps {
  group: DocumentType<typeof ADD_MEMBER_SELECT_FRAGMENT>;
  onAddGroup: (id: string) => Promise<unknown>;
  onAddUser: (id: string) => Promise<unknown>;
  onDismiss: () => void;
}

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getIdSet = (arr: any[]) =>
  new Set(arr.map(({ id = null } = {}) => id).filter(Boolean) as string[]);

const AddMemberSelect = ({
  group,
  onAddGroup,
  onAddUser,
  onDismiss,
}: AddMemberSelectProps) => {
  const dispatch = useAppDispatch();

  const [existingMemberIds, existingSubgroupIds] = useMemo(() => {
    const owners = group?.owners || [];
    const members = group?.members || [];
    const children = group?.children || [];
    const userIdSet = getIdSet([...members, ...owners]);
    const subgroupIdSet = getIdSet(children);

    return [userIdSet, subgroupIdSet];
  }, [group]);

  const config = useMemo<EntitySelectConfig>(
    () => ({
      user: {
        filter: (user) => !existingMemberIds.has(user.id),
        extra: () => ({ showBadge: true }),
      },
      group: {
        filter: (g) =>
          !!g?.id && g.id !== group?.id && !existingSubgroupIds.has(g.id),
      },
    }),
    [existingMemberIds, existingSubgroupIds, group?.id],
  );

  const handleAddUser = ({
    id,
    name,
  }: {
    id: string;
    name: string;
    type?: UserType | null;
  }) =>
    onAddUser(id).then(() =>
      dispatch(
        UIActions.pushToast({
          level: 'success',
          message: `${name} was added as a member`,
        }),
      ),
    );

  const handleAddGroup = ({ id, name }: { id: string; name: string }) =>
    onAddGroup(id)
      .then(() =>
        dispatch(
          UIActions.pushToast({
            level: 'success',
            message: `${name} was added as a subgroup`,
          }),
        ),
      )
      .catch((error) => {
        const showError = (message: string) =>
          dispatch(UIActions.pushToast({ level: 'error', message }));

        handleApolloError(error, {
          CIRCULAR_DESCENDANT: () => {
            showError('Cannot create circular group membership.');
          },
        });
      });

  const handleAdd = (entity: EntitySearchResult | null) => {
    if (entity?.type === 'user') handleAddUser(entity.user);
    if (entity?.type === 'group') handleAddGroup(entity.group);
  };

  return (
    <div className={styles.container}>
      <EntitySelect
        isClearable={false}
        menuPlacement="top"
        onChange={handleAdd}
        config={config}
        value={null}
      />
      <Button onClick={onDismiss} variant="muted">
        <div className={styles.buttonInner}>
          <SVGIcon size="small">{clearIcon}</SVGIcon>
        </div>
      </Button>
    </div>
  );
};

export default AddMemberSelect;
